import React, { memo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';

// ─── Scene 1 config ────────────────────────────────────────────────────────────
const S1_FRAMES     = 120;
const S1_SCROLL     = typeof window !== 'undefined' ? window.innerHeight * 4 : 3000;
const S1_FRAME_PATH = (n) => {
  const frame = String(n).padStart(3, '0');
  return [
    `/images/scroll/ezgif-frame-${frame}.jpg`,
    `/images/scroll/ezgif-frame-${frame}.png`,
  ];
};

// ─── Scene 2 config ────────────────────────────────────────────────────────────
const S2_FRAMES     = 100;
const S2_SCROLL     = typeof window !== 'undefined' ? window.innerHeight * 5.5 : 4200;
const S2_FRAME_PATH = (n) => `/images/scroll2/frame-${String(n).padStart(3, '0')}.jpg`;

// How many pixels the two scenes overlap during crossfade
const CROSSFADE_PX  = typeof window !== 'undefined' ? window.innerHeight * 0.5 : 400;
const sceneScrollHeight = (scrollDistance) => `calc(100vh + ${scrollDistance}px)`;
const RECOMMENDED_LOGOS = [
  { name: 'Bounty Fresh', src: '/logos_white/334-3344439_bounty-fresh-hd-png-download.png' },
  { name: 'Bureau of Animal Industry', src: '/logos_white/BAI.png' },
  { name: 'GAMA', src: '/logos_white/GAMA.png' },
  { name: 'National Livestock Program', src: '/logos_white/NLP.png' },
  { name: 'Philippine Statistics Authority', src: '/logos_white/PSA.png' },
];
const PILOT_TEST_IMAGES = [
  '/pilottestings/BIRDSEYE%20DISRUPTORX.jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(1).jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(2).jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(3).jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(4).jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(5).jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(6).jpg',
  '/pilottestings/BIRDSEYE%20DISRUPTORX%20(7).jpg',
];
const FEATURE_USE_CASES = [
  { icon: 'droplet', title: 'Wet Litter Detection', copy: 'Real-time hybrid vision and deep learning catches issues early.' },
  { icon: 'gas', title: 'Ammonia Monitoring', copy: 'Tracks ground-level ammonia in ppm where bird health is affected.' },
  { icon: 'trend', title: 'Wetness Growth Rate', copy: 'Monitors how quickly litter conditions are worsening.' },
  { icon: 'birdCount', title: 'Bird Count Tracking', copy: 'Automatically counts chickens per session alongside conditions.' },
];
const FEATURE_INDUSTRIES = [
  { icon: 'historyChart', title: 'Historical Trend Analysis', copy: 'View wetness and ammonia trends across 24 hours to 7 days.' },
  { icon: 'sms', title: 'SMS Alerts & Recommendations', copy: 'Sends critical alerts with recommended actions to the operator.' },
  { icon: 'insight', title: 'Actionable Insights', copy: 'Explains what the numbers mean and what likely comes next.' },
];
const TECH_GLOW_COLORS = {
  orange: '#FF7A2F',
  blue: '#3BA8FF',
};
const TECH_STACK_SKILLS = [
  {
    id: 'python',
    iconType: 'python',
    label: 'Python',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg',
    glowColor: 'orange',
    orbitRadius: 100,
    size: 54,
    speed: 1,
    phaseShift: 0,
  },
  {
    id: 'react',
    iconType: 'react',
    label: 'React',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg',
    glowColor: 'blue',
    orbitRadius: 100,
    size: 54,
    speed: 1,
    phaseShift: (Math.PI * 2) / 3,
  },
  {
    id: 'javascript',
    iconType: 'javascript',
    label: 'Javascript',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg',
    glowColor: 'orange',
    orbitRadius: 100,
    size: 52,
    speed: 1,
    phaseShift: (Math.PI * 4) / 3,
  },
  {
    id: 'css',
    iconType: 'css',
    label: 'CSS',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg',
    glowColor: 'blue',
    orbitRadius: 180,
    size: 52,
    speed: -0.6,
    phaseShift: 0,
  },
  {
    id: 'pytorch',
    iconType: 'pytorch',
    label: 'Pytorch',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pytorch/pytorch-original.svg',
    glowColor: 'orange',
    orbitRadius: 180,
    size: 56,
    speed: -0.6,
    phaseShift: Math.PI / 2,
  },
  {
    id: 'fastapi',
    iconType: 'fastapi',
    label: 'FASTAPI',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/fastapi/fastapi-original.svg',
    glowColor: 'blue',
    orbitRadius: 180,
    size: 52,
    speed: -0.6,
    phaseShift: Math.PI,
  },
  {
    id: 'google-cloud',
    iconType: 'google-cloud',
    label: 'Google Cloud',
    src: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg',
    glowColor: 'orange',
    orbitRadius: 180,
    size: 58,
    speed: -0.6,
    phaseShift: (Math.PI * 3) / 2,
  },
];
const FOOTER_LINK_GROUPS = [
  {
    title: 'Product',
    links: [
      { label: 'Features', href: '#features' },
      { label: 'How it works', href: '#how-it-works' },
      { label: 'Live demo', href: '#live-demo' },
      { label: 'Pilot testing', href: '#pilot-testing' },
      { label: 'Tech stack', href: '#tech-stack' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Dashboard', href: '/login' },
      { label: 'Get started', href: '/signup' },
      { label: 'Farm monitoring', href: '#features' },
      { label: 'System validation', href: '#pilot-testing' },
    ],
  },
  {
    title: 'More',
    links: [
      { label: 'About', href: '#how-it-works' },
      { label: 'Contact', href: '#contact-us' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'Privacy Policy', href: '#privacy-policy' },
      { label: 'Terms of Use', href: '#terms-of-use' },
    ],
  },
];

// ─── Utilities ─────────────────────────────────────────────────────────────────
function preloadFrames(pathFn, total, onProgress) {
  const imgs = new Array(total);
  let loaded = 0;
  return new Promise((resolve) => {
    const markLoaded = () => {
      loaded++;
      onProgress?.(loaded, total);
      if (loaded === total) resolve(imgs);
    };

    for (let i = 0; i < total; i++) {
      const img = new Image();
      const candidates = [pathFn(i + 1)].flat();
      let candidateIndex = 0;
      img.onload = markLoaded;
      img.onerror = () => {
        candidateIndex++;
        if (candidateIndex < candidates.length) {
          img.src = candidates[candidateIndex];
        } else {
          markLoaded();
        }
      };
      img.src = candidates[candidateIndex];
      imgs[i] = img;
    }
  });
}

function drawCover(canvas, img) {
  if (!img?.complete || !img.naturalWidth) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const scale = Math.max(W / img.naturalWidth, H / img.naturalHeight);
  const dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
  ctx.clearRect(0, 0, W, H);
  ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
}

function FeatureMenuIcon({ type }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {type === 'droplet' && (
        <>
          <path d="M12 3.5s6 6.2 6 10.5a6 6 0 0 1-12 0c0-4.3 6-10.5 6-10.5Z" {...common} />
          <path d="M8.8 14.6c.5 1.8 1.7 2.7 3.6 2.7" {...common} />
        </>
      )}
      {type === 'gas' && (
        <>
          <path d="M4 15c2.4-2 4.7-2 7 0s4.6 2 7 0" {...common} />
          <path d="M4 10c2.4-2 4.7-2 7 0s4.6 2 7 0" {...common} />
          <path d="M7 19h10" {...common} />
          <path d="M9 5h6" {...common} />
        </>
      )}
      {type === 'trend' && (
        <>
          <path d="M4 18h16" {...common} />
          <path d="m5 15 4-4 3 3 6-7" {...common} />
          <path d="M14 7h4v4" {...common} />
          <path d="M6.5 7.5s2.4 2.5 2.4 4.2a2.4 2.4 0 0 1-4.8 0c0-1.7 2.4-4.2 2.4-4.2Z" {...common} />
        </>
      )}
      {type === 'birdCount' && (
        <>
          <path d="M8 15c-2.3-.4-4-2-4-4.1 0-2.4 2.3-4.4 5.1-4.4 2 0 3.8 1 4.6 2.4" {...common} />
          <path d="M13 9c2.3 0 4.2 1.7 4.2 3.8S15.3 16.6 13 16.6 8.8 14.9 8.8 12.8 10.7 9 13 9Z" {...common} />
          <path d="m17.1 11.2 2.9-1.1-1.3 2.7" {...common} />
          <path d="M9 18h8M10.5 18v2M15.5 18v2" {...common} />
          <path d="M7.2 9.3h.1M12.2 11.8h.1" {...common} />
        </>
      )}
      {type === 'historyChart' && (
        <>
          <path d="M4 5v14h16" {...common} />
          <path d="M7 15c1.4-3.4 3.2-4.5 5.3-3.2 2 1.2 3.7.4 5.2-2.3" {...common} />
          <path d="M7 8h3M7 11h2" {...common} />
          <path d="M18 5v3h-3" {...common} />
        </>
      )}
      {type === 'sms' && (
        <>
          <rect x="7" y="3" width="10" height="18" rx="2" {...common} />
          <path d="M10 6h4M11 18h2" {...common} />
          <path d="M9.5 10.5h5M9.5 13h3" {...common} />
        </>
      )}
      {type === 'insight' && (
        <>
          <path d="M9 18h6" {...common} />
          <path d="M10 21h4" {...common} />
          <path d="M8.5 14.5c-1.2-1-2-2.5-2-4.1a5.5 5.5 0 0 1 11 0c0 1.7-.8 3.1-2 4.1-.8.7-1.2 1.4-1.3 2.5H9.8c-.1-1.1-.5-1.8-1.3-2.5Z" {...common} />
          <path d="m11 10 1.2 1.2L15 8.4" {...common} />
        </>
      )}
      {type === 'cap' && (
        <>
          <path d="m3 9 9-5 9 5-9 5-9-5Z" {...common} />
          <path d="M7 12v4c2.8 2 7.2 2 10 0v-4" {...common} />
        </>
      )}
      {type === 'screen' && (
        <>
          <rect x="4" y="5" width="16" height="11" rx="1.5" {...common} />
          <path d="M8 20h8M12 16v4" {...common} />
        </>
      )}
      {type === 'users' && (
        <>
          <circle cx="9" cy="9" r="3" {...common} />
          <path d="M3.5 20c.8-3 2.8-4.5 5.5-4.5S13.7 17 14.5 20" {...common} />
          <path d="M15 7.5a2.5 2.5 0 0 1 0 5M16.5 15.6c1.8.6 3.1 2 3.8 4.4" {...common} />
        </>
      )}
    </svg>
  );
}

const OrbitingTechSkill = memo(({ skill, time }) => {
  const angle = time * skill.speed + skill.phaseShift;
  const x = Math.cos(angle) * skill.orbitRadius;
  const y = Math.sin(angle) * skill.orbitRadius;

  return (
    <div
      className="tech-orbit-skill"
      style={{
        '--skill-x': `${x}px`,
        '--skill-y': `${y}px`,
        '--skill-size': `${skill.size}px`,
        '--skill-color': TECH_GLOW_COLORS[skill.glowColor] || TECH_GLOW_COLORS.orange,
      }}
    >
      <div className="tech-orbit-icon">
        <img src={skill.src} alt={`${skill.label} logo`} loading="lazy" decoding="async" />
      </div>
      <span className="tech-orbit-label">{skill.label}</span>
    </div>
  );
});

OrbitingTechSkill.displayName = 'OrbitingTechSkill';

function TechStackOrbit() {
  const [time, setTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (isPaused || prefersReducedMotion) return undefined;

    let frameId;
    let lastTime = performance.now();
    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;
      setTime((prevTime) => prevTime + deltaTime);
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [isPaused]);

  return (
    <div
      className="tech-orbit-stage"
      aria-label="Birdseye technology stack"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="tech-orbit-core" aria-hidden="true">
        <img src="/images/logo.png" alt="" />
      </div>
      <span className="tech-orbit-path inner" />
      <span className="tech-orbit-path outer" />
      {TECH_STACK_SKILLS.map((skill) => (
        <OrbitingTechSkill key={skill.id} skill={skill} time={time} />
      ))}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();

  // Scene 1
  const s1WrapperRef  = useRef(null);
  const s1SceneRef    = useRef(null);
  const s1CanvasRef   = useRef(null);
  const s1IntroRef    = useRef(null);
  const s1HeroRef     = useRef(null);
  const s1VigRef      = useRef(null);
  const s1Frames      = useRef([]);
  const s1RenderedIdx = useRef(-1);

  // Scene 2
  const s2WrapperRef  = useRef(null);
  const s2SceneRef    = useRef(null);
  const s2CanvasRef   = useRef(null);
  const s2IntroRef    = useRef(null);
  const s2VigRef      = useRef(null);
  const s2Frames      = useRef([]);
  const s2RenderedIdx = useRef(-1);

  // Loader
  const loaderRef    = useRef(null);
  const loaderBarRef = useRef(null);
  const ditherCanvasRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const s1Canvas = s1CanvasRef.current;
    const s2Canvas = s2CanvasRef.current;
    const loaderEl = loaderRef.current;
    const s1IntroEl = s1IntroRef.current;
    if (!s1Canvas || !s2Canvas) return undefined;

    // ── Resize ────────────────────────────────────────────────────────────────
    function resize() {
      if (!isMounted) return;
      s1Canvas.width  = s2Canvas.width  = window.innerWidth;
      s1Canvas.height = s2Canvas.height = window.innerHeight;
      if (s1Frames.current.length) {
        const i = Math.max(0, s1RenderedIdx.current);
        drawCover(s1Canvas, s1Frames.current[i]);
      }
      if (s2Frames.current.length) {
        const i = Math.max(0, s2RenderedIdx.current);
        drawCover(s2Canvas, s2Frames.current[i]);
      }
    }
    resize();
    window.addEventListener('resize', resize);

    function onScroll() {
      if (!isMounted) return;
      const s1Wrapper = s1WrapperRef.current;
      const s1Scene = s1SceneRef.current;
      const s2Wrapper = s2WrapperRef.current;
      const s2Scene = s2SceneRef.current;
      if (!s1Wrapper || !s1Scene || !s2Wrapper || !s2Scene) return;
      // ── Scene 1 ─────────────────────────────────────────────────────────────
      const s1Rect = s1Wrapper.getBoundingClientRect();
      const s1Top = s1Rect?.top ?? 0;
      const s1Bottom = s1Rect?.bottom ?? 0;
      const s1Entered = s1Top <= 0;
      const s1Exited = s1Bottom <= window.innerHeight;

      if (s1Entered && !s1Exited) {
        const raw  = Math.min(Math.abs(s1Top), S1_SCROLL);
        const prog = raw / S1_SCROLL;

        s1Scene.style.position = 'fixed';
        s1Scene.style.top      = '0';
        s1Scene.style.left     = '0';
        s1Scene.style.pointerEvents = 'auto';

        // Frame
        const idx = Math.round(prog * (S1_FRAMES - 1));
        if (idx !== s1RenderedIdx.current) {
          drawCover(s1Canvas, s1Frames.current[idx]);
          s1RenderedIdx.current = idx;
        }

        // Crossfade out at the very end of Scene 1
        const crossAlpha = Math.max(0, 1 - (prog - (1 - CROSSFADE_PX / S1_SCROLL)) / (CROSSFADE_PX / S1_SCROLL));
        s1Scene.style.opacity = crossAlpha;

        // Intro text: fade out over first 25%
        if (s1IntroRef.current) {
          const a = Math.max(0, 1 - prog * 4);
          s1IntroRef.current.style.opacity   = a;
          s1IntroRef.current.style.transform = `translateY(${(1 - a) * -20}px)`;
        }

        // Hero text: fade in after the intro, hold full opacity, then slide up during crossfade.
        const heroA = Math.min(1, Math.max(0, (prog - 0.28) / 0.18)); // fully opaque by 46%
        const crossStart = 1 - CROSSFADE_PX / S1_SCROLL;
        const heroScrollProg = Math.max(0, (prog - crossStart) / (1 - crossStart)); // 0→1 during crossfade
        const heroTranslate  = heroScrollProg * -160; // slides up 160px during crossfade, stays fully visible
        if (s1HeroRef.current) {
          s1HeroRef.current.style.opacity   = heroA;
          s1HeroRef.current.style.transform = `translateY(${heroA < 1 ? (1 - heroA) * 24 : heroTranslate}px)`;
        }
        if (s1VigRef.current) s1VigRef.current.style.opacity = heroA;

      } else if (s1Exited) {
        s1Scene.style.position = 'absolute';
        s1Scene.style.top      = `${S1_SCROLL}px`;
        s1Scene.style.left     = '0';
        s1Scene.style.opacity  = '0';
        s1Scene.style.pointerEvents = 'none';
      } else {
        // Before Scene 1 starts
        s1Scene.style.position = 'relative';
        s1Scene.style.top      = '';
        s1Scene.style.left     = '';
        s1Scene.style.opacity  = '1';
        s1Scene.style.pointerEvents = 'auto';
        drawCover(s1Canvas, s1Frames.current[0]);
        s1RenderedIdx.current = 0;
        if (s1IntroRef.current) { s1IntroRef.current.style.opacity = '1'; s1IntroRef.current.style.transform = 'none'; }
        if (s1HeroRef.current)  { s1HeroRef.current.style.opacity = '0'; s1HeroRef.current.style.transform = 'translateY(24px)'; }
        if (s1VigRef.current)   { s1VigRef.current.style.opacity   = '0'; }
      }
// ── Scene 2 ─────────────────────────────────────────────────────────────
const s2Rect = s2Wrapper.getBoundingClientRect();

const s2Top = s2Rect.top;
const s2Bottom = s2Rect.bottom;

const s2Entered = s2Top <= 0;
const s2Exited  = s2Bottom <= 0;

if (s2Entered && !s2Exited) {
  const raw  = Math.min(Math.abs(s2Top), S2_SCROLL);
  const prog = raw / S2_SCROLL;

  // Pin scene
  s2Scene.style.position = 'fixed';
  s2Scene.style.top      = '0';
  s2Scene.style.left     = '0';
  s2Scene.style.pointerEvents = 'auto';

  s2Scene.style.opacity = '1';

  // Frame
  const idx = Math.round(prog * (S2_FRAMES - 1));
  if (idx !== s2RenderedIdx.current) {
    drawCover(s2Canvas, s2Frames.current[idx]);
    s2RenderedIdx.current = idx;
  }

  // Intro text
  if (s2IntroRef.current) {
    const scrollProg = Math.min(1, Math.max(0, (prog - 0.06) / 0.88));
    const overflowTravel = Math.max(0, s2IntroRef.current.scrollHeight - window.innerHeight + 96);
    const travel = Math.min(760, overflowTravel);
    s2IntroRef.current.style.transform = `translateY(${scrollProg * -travel}px)`;
  }

} else if (s2Exited) {
  // 🔥 THIS LINE FIXES YOUR PROBLEM
  s2Scene.style.position = 'absolute';
  s2Scene.style.top      = `${S2_SCROLL}px`;
  s2Scene.style.left     = '0';
  s2Scene.style.opacity  = '1';
  s2Scene.style.pointerEvents = 'none';

} else {
  // Before entering
  s2Scene.style.position = 'relative';
  s2Scene.style.top      = '';
  s2Scene.style.left     = '';
  s2Scene.style.opacity  = '1';
  s2Scene.style.pointerEvents = 'auto';

  if (s2VigRef.current)  s2VigRef.current.style.opacity  = '0';
  if (s2IntroRef.current) s2IntroRef.current.style.transform = 'none';
}
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    // ── Preload both frame sets ────────────────────────────────────────────────
    const totalFrames = S1_FRAMES + S2_FRAMES;
    let s1LoadedCount = 0;
    let s2LoadedCount = 0;

    function updateLoader() {
      const totalLoaded = s1LoadedCount + s2LoadedCount;
      if (loaderBarRef.current)
        loaderBarRef.current.style.width = `${((totalLoaded / totalFrames) * 100).toFixed(1)}%`;
    }

    Promise.all([
      preloadFrames(S1_FRAME_PATH, S1_FRAMES, (loaded) => {
        s1LoadedCount = loaded;
        updateLoader();
      }),
      preloadFrames(S2_FRAME_PATH, S2_FRAMES, (loaded) => {
        s2LoadedCount = loaded;
        updateLoader();
      }),
    ]).then(([s1imgs, s2imgs]) => {
      if (!isMounted) return;
      s1Frames.current = s1imgs;
      s2Frames.current = s2imgs;

      drawCover(s1Canvas, s1imgs[0]);
      s1RenderedIdx.current = 0;
      drawCover(s2Canvas, s2imgs[0]);
      s2RenderedIdx.current = 0;

      // Hide loader
      if (loaderRef.current) {
        gsap.to(loaderRef.current, {
          opacity: 0, duration: 0.5,
          onComplete: () => { if (loaderRef.current) loaderRef.current.style.display = 'none'; },
        });
      }

      // Entrance animations
      gsap.fromTo('#birdseye-nav',
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out', delay: 0.2 }
      );
      if (s1IntroRef.current) {
        const introPieces = s1IntroRef.current.querySelectorAll('.intro-reveal-piece');
        gsap.fromTo(s1IntroRef.current,
          { opacity: 1 },
          { opacity: 1, duration: 0.01 }
        );
        gsap.fromTo(introPieces,
          { opacity: 0, y: 28, filter: 'blur(22px)', scale: 0.98 },
          {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            scale: 1,
            duration: 1.25,
            ease: 'power3.out',
            stagger: 0.16,
            delay: 0.45,
          }
        );
      }
    });

    return () => {
      isMounted = false;
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      gsap.killTweensOf([loaderEl, '#birdseye-nav', s1IntroEl, '.intro-reveal-piece']);
    };
  }, []);

  // ── Tilt handler for demo card ─────────────────────────────────────────────
  useEffect(() => {
    const canvas = ditherCanvasRef.current;
    const section = canvas?.closest('.demo-section');
    const ctx = canvas?.getContext('2d');
    if (!canvas || !section || !ctx) return undefined;

    const pointer = { active: false, x: 0, y: 0 };
    const eased = { x: 0, y: 0 };
    const dots = [];
    const rows = [];
    let width = 0;
    let height = 0;
    let dpr = 1;
    let frameId;

    function resizeDither() {
      const rect = section.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      dots.length = 0;
      rows.length = 0;
      const spacing = width < 760 ? 24 : 30;
      const offsetX = (width % spacing) / 2;
      const offsetY = (height % spacing) / 2;
      let rowIndex = 0;
      for (let y = offsetY; y <= height; y += spacing * 0.86) {
        const row = [];
        const stagger = rowIndex % 2 ? spacing / 2 : 0;
        for (let x = offsetX + stagger; x <= width; x += spacing) {
          const seed = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
          const dot = {
            baseX: x,
            baseY: y,
            x,
            y,
            heat: 0,
            noise: seed - Math.floor(seed),
          };
          dots.push(dot);
          row.push(dot);
        }
        rows.push(row);
        rowIndex++;
      }
    }

    function onPointerMove(e) {
      const rect = section.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      pointer.active = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
      pointer.x = x;
      pointer.y = y;
    }

    function onPointerLeave(e) {
      const next = e.relatedTarget;
      if (!next || !section.contains(next)) {
        pointer.active = false;
      }
    }

    function animateDither(time) {
      const t = time * 0.001;
      eased.x += (pointer.x - eased.x) * 0.18;
      eased.y += (pointer.y - eased.y) * 0.18;
      ctx.clearRect(0, 0, width, height);

      for (const dot of dots) {
        const drift = 4 + dot.noise * 5;
        dot.x = dot.baseX + Math.sin(t * 0.8 + dot.noise * 9) * drift;
        dot.y = dot.baseY + Math.cos(t * 0.65 + dot.noise * 11) * drift;

        const dx = dot.x - eased.x;
        const dy = dot.y - eased.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const halo = pointer.active ? Math.max(0, 1 - distance / 230) : 0;
        const core = pointer.active ? Math.max(0, 1 - distance / 86) : 0;
        dot.heat += (Math.max(halo, core) - dot.heat) * 0.18;
      }

      function drawLine(a, b) {
        const heat = Math.max(a.heat, b.heat);
        const idle = 0.025 + (a.noise + b.noise) * 0.012;
        const opacity = Math.min(0.5, idle + heat * 0.42);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = `rgba(145,12,18,${opacity})`;
        ctx.lineWidth = 0.55 + heat * 1.25;
        ctx.stroke();
      }

      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        const nextRow = rows[r + 1];
        for (let c = 0; c < row.length; c++) {
          const dot = row[c];
          const right = row[c + 1];
          const down = nextRow?.[c];
          const diagonal = nextRow?.[r % 2 ? c + 1 : c - 1];

          if (right) drawLine(dot, right);
          if (down) drawLine(dot, down);
          if (diagonal) drawLine(dot, diagonal);

          if (right && down && (dot.heat > 0.02 || right.heat > 0.02 || down.heat > 0.02)) {
            const heat = Math.max(dot.heat, right.heat, down.heat);
            ctx.beginPath();
            ctx.moveTo(dot.x, dot.y);
            ctx.lineTo(right.x, right.y);
            ctx.lineTo(down.x, down.y);
            ctx.closePath();
            ctx.fillStyle = `rgba(145,12,18,${Math.min(0.14, heat * 0.12)})`;
            ctx.fill();
          }
        }
      }

      for (const dot of dots) {
        const pulse = 0.5 + Math.sin(t * 2 + dot.noise * 8) * 0.5;
        const opacity = Math.min(1, 0.06 + dot.noise * 0.05 + dot.heat * 0.86);
        const radius = 0.8 + dot.noise * 0.48 + dot.heat * 2.6 + pulse * dot.heat * 0.52;

        ctx.beginPath();
        ctx.fillStyle = `rgba(180,18,24,${opacity})`;
        ctx.arc(dot.x, dot.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      if (pointer.active) {
        const glow = ctx.createRadialGradient(eased.x, eased.y, 0, eased.x, eased.y, 260);
        glow.addColorStop(0, 'rgba(180,18,24,0.22)');
        glow.addColorStop(0.34, 'rgba(145,12,18,0.09)');
        glow.addColorStop(1, 'rgba(145,12,18,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, width, height);
      }

      frameId = requestAnimationFrame(animateDither);
    }

    resizeDither();
    pointer.x = width * 0.78;
    pointer.y = height * 0.18;
    eased.x = pointer.x;
    eased.y = pointer.y;
    frameId = requestAnimationFrame(animateDither);
    window.addEventListener('resize', resizeDither);
    section.addEventListener('pointermove', onPointerMove);
    section.addEventListener('pointerleave', onPointerLeave);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resizeDither);
      section.removeEventListener('pointermove', onPointerMove);
      section.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  useEffect(() => {
    const revealEls = document.querySelectorAll('.demo-reveal, .s2-reveal, .live-demo-reveal, .pilot-reveal, .tech-reveal');
    if (!revealEls.length) return undefined;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        } else {
          entry.target.classList.remove('is-visible');
        }
      }
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px 12% 0px',
    });

    revealEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleTiltMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    const rotX = -dy * 8;
    const rotY =  dx * 12;
    card.style.transform = `perspective(1200px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale3d(1.02,1.02,1.02)`;
    const shine = card.querySelector('.demo-shine');
    if (shine) {
      shine.style.background = `radial-gradient(circle at ${(dx + 1) * 50}% ${(dy + 1) * 50}%, rgba(255,255,255,0.10) 0%, transparent 65%)`;
    }
  };

  const handleTiltLeave = (e) => {
    const card = e.currentTarget;
    card.style.transform = 'perspective(1200px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    const shine = card.querySelector('.demo-shine');
    if (shine) shine.style.background = 'none';
  };

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { overflow-x: hidden; }
        body {
          background: #000; color: #fff;
          font-family: 'DM Sans', sans-serif;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        /* ── Loader ── */
        .loader-overlay {
          position: fixed; inset: 0; z-index: 999; background: #000;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column; gap: 20px;
        }
        .loader-label { font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(255,255,255,0.3); }
        .loader-track { width: 200px; height: 1px; background: rgba(255,255,255,0.1); overflow: hidden; }
        .loader-bar   { height: 100%; width: 0%; background: linear-gradient(90deg,#FF2525,#FF8A00); transition: width 0.1s linear; }

        /* ── Nav ── */
        #birdseye-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          padding: 0 40px; height: 68px; display: flex; align-items: center;
          background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%);
          opacity: 0;
        }
        .nav-inner {
          width: 100%; max-width: 1120px; margin: 0 auto;
          display: grid; grid-template-columns: auto 1fr auto; gap: 24px; align-items: center;
        }
        .nav-links { display: flex; justify-content: center; gap: 32px; list-style: none; }
        .nav-links > li { position: relative; }
        .nav-links a { color: rgba(255,255,255,0.65); font-size: 13px; text-decoration: none; transition: color 0.18s; }
        .nav-links a:hover { color: #fff; }
        .features-nav-item {
          position: static;
          padding: 24px 0;
          margin: -24px 0;
        }
        .features-mega {
          position: fixed;
          top: 68px;
          left: 50%;
          width: min(700px, calc(100vw - 88px));
          min-height: 282px;
          display: grid;
          grid-template-columns: 1.25fr 1.25fr 0.96fr;
          gap: 16px;
          padding: 0;
          border-radius: 18px;
          background: #fff;
          box-shadow: 0 26px 60px rgba(0,0,0,0.18);
          color: #303034;
          font-family: 'Inter', 'DM Sans', sans-serif;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transform: translate3d(-50%, -8px, 0);
          transition:
            opacity 0.18s ease,
            visibility 0.18s ease,
            transform 0.18s ease;
          overflow: hidden;
        }
        .features-nav-item:hover .features-mega,
        .features-nav-item:focus-within .features-mega {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translate3d(-50%, 0, 0);
        }
        .features-mega-column {
          padding: 16px 16px 18px 16px;
        }
        .features-mega-column + .features-mega-column {
          border-left: 1px solid #ededed;
        }
        .features-mega-heading {
          display: block;
          margin-bottom: 12px;
          color: #939393;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.1;
        }
        .features-menu-list {
          display: grid;
          gap: 12px;
          list-style: none;
        }
        .features-menu-link {
          display: grid;
          grid-template-columns: 36px 1fr;
          gap: 10px;
          align-items: center;
          color: inherit;
          text-decoration: none;
        }
        .features-menu-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 9px;
          background: #f5f5f5;
          color: #242424;
          transition: background 0.18s ease, transform 0.18s ease;
        }
        .features-menu-icon svg {
          width: 17px;
          height: 17px;
          display: block;
        }
        .features-menu-link:hover .features-menu-icon,
        .features-menu-link:focus-visible .features-menu-icon {
          background: #eeeeee;
          transform: translateY(-1px);
        }
        .features-menu-title {
          display: block;
          color: #2f2f33;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.15;
        }
        .features-menu-copy {
          display: block;
          margin-top: 2px;
          color: #969696;
          font-size: 9.5px;
          font-weight: 300;
          line-height: 1.28;
        }
        .features-mega-promo {
          padding: 14px;
          background: #f4f4f4;
          border-radius: 18px;
        }
        .features-promo-image {
          height: 128px;
          border-radius: 10px;
          background: url('/dropdown.jpg') center / cover no-repeat;
          position: relative;
          overflow: hidden;
        }
        .features-promo-image::before {
          content: none;
        }
        .features-promo-image::after {
          content: none;
        }
        .features-promo-brand {
          margin-top: 14px;
          color: #141414;
          font-size: 13px;
          font-weight: 500;
          line-height: 1;
        }
        .features-promo-brand em {
          font-family: 'Playfair Display', serif;
          font-weight: 500;
        }
        .features-promo-copy {
          margin-top: 14px;
          max-width: 165px;
          color: #202020;
          font-size: 11px;
          font-weight: 500;
          line-height: 1.38;
        }
        .features-promo-button {
          margin-top: 20px;
          min-height: 28px;
          padding: 0 13px;
          border: 1.5px solid #656565;
          border-radius: 8px;
          background: transparent;
          color: #333;
          font-family: 'Inter', 'DM Sans', sans-serif;
          font-size: 9.5px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
        }
        .features-promo-button:hover {
          background: #111;
          border-color: #111;
          color: #fff;
        }
        .nav-actions { display: flex; align-items: center; gap: 12px; }
        .btn-ghost {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,0.65); font-family: 'DM Sans', sans-serif;
          font-size: 13px; transition: color 0.18s; display: flex; align-items: center; gap: 8px;
        }
        .btn-ghost:hover { color: #fff; }
        .nav-brand { gap: 4px; }
        .btn-nav {
          padding: 8px 22px; border-radius: 100px; border: none; cursor: pointer;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500; color: #fff;
          background: linear-gradient(135deg, #FF2525, #8B1010);
          box-shadow: 0 0 22px rgba(255,37,37,0.35);
          transition: transform 0.2s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s;
        }
        .btn-nav:hover { transform: scale(1.06); box-shadow: 0 0 40px rgba(255,37,37,0.55); }

        /* ── Scene wrappers & canvases ── */
        .sticky-wrapper { position: relative; width: 100vw; height: 100vh; }
        .sticky-scene   { position: relative; width: 100vw; height: 100vh; overflow: hidden; background: #000; }
        .sticky-canvas  { position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: block; z-index: 1; }

        /* Scene 2 stacks on top via z-index so its crossfade overlays Scene 1 */
        .sticky-scene.scene-2 { z-index: 10; opacity: 0; }
        .sticky-scene.scene-2::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: clamp(140px, 24vh, 280px);
          z-index: 4;
          pointer-events: none;
          background: linear-gradient(180deg, #000 0%, rgba(0,0,0,0.78) 32%, rgba(0,0,0,0.28) 68%, rgba(0,0,0,0) 100%);
        }

        /* ── Vignette ── */
        .bottom-vignette {
          position: absolute; inset: 0; z-index: 2; pointer-events: none; opacity: 0;
          background:
            radial-gradient(ellipse 120% 60% at 50% 100%, rgba(0,0,0,0.92) 0%, transparent 65%),
            radial-gradient(ellipse 80%  50% at 50% 100%, rgba(0,0,0,0.6)  0%, transparent 55%);
          will-change: opacity;
        }

        /* ── Overlay text base ── */
        .overlay-text {
          position: absolute; inset: 0; z-index: 3;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column; gap: 16px; text-align: center;
          pointer-events: none; will-change: opacity, transform;
        }

        /* ── Scene 1: intro ── */
        .intro-text { opacity: 0; }
        .intro-headline {
          font-family: 'Playfair Display', serif;
          font-size: clamp(34px, 4.8vw, 66px);
          font-weight: 500;
          font-style: italic;
          line-height: 0.95;
          letter-spacing: -0.03em;
          color: #fff;
        }

        /* ── Scene 1: hero ── */
        .intro-reveal-piece {
          display: inline-block;
          will-change: opacity, transform, filter;
        }
        .hero-text {
          opacity: 0;
          align-items: center;
          justify-content: center !important;
          padding-bottom: 0;
          margin-top: 0;
        }
        .hero-headline {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(38px, 5vw, 62px);
          font-weight: 500;
          line-height: 0.95;
          letter-spacing: -1.5px;
          color: #fff;
          text-shadow: 0 2px 40px rgba(0,0,0,0.6);
        }
        .hero-headline .accent { color: #FF2020; }
        .hero-sub {
          font-family: 'Inter', sans-serif;
          font-size: 15px; font-weight: 300; line-height: 1.40;
          color: rgba(255,255,255,0.48); max-width: 400px;
          margin-top: -8px;
        }
        .hero-cta-row { display: flex; gap: 12px; align-items: center; margin-top: 10px; pointer-events: all; }
        .btn-cta-primary {
          padding: 11px 28px; border-radius: 100px; border: none; cursor: pointer;
          font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 500; color: #fff;
          background: linear-gradient(135deg, #FF2525, #8B1010);
          box-shadow: 0 0 30px rgba(255,37,37,0.28), 0 2px 12px rgba(0,0,0,0.3);
          transition: transform 0.2s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s;
        }
        .btn-cta-primary:hover { transform: scale(1.07) translateY(-1px); box-shadow: 0 0 44px rgba(255,37,37,0.5), 0 4px 20px rgba(0,0,0,0.4); }
        .btn-cta-ghost {
          padding: 11px 28px; border-radius: 100px; cursor: pointer;
          font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 400;
          color: rgba(255,255,255,0.85);
          background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(14px);
          transition: transform 0.2s cubic-bezier(.34,1.56,.64,1), background 0.2s;
        }
        .btn-cta-ghost:hover { transform: scale(1.07) translateY(-1px); background: rgba(255,255,255,0.12); }

        /* ── Scene 2: intro & hero ── */
        .scene-2 .intro-text {
          z-index: 5;
          justify-content: flex-start;
          padding-top: clamp(76px, 10vh, 110px);
          padding-bottom: clamp(90px, 12vh, 140px);
        }
        .s2-intro-headline {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(28px, 3.6vw, 52px);
          font-weight: 300;
          line-height: 1.1;
          letter-spacing: -0.02em;
          color: rgba(255,255,255,0.9);
          margin: 0;
        }
        .s2-intro-headline strong {
          font-weight: inherit;
          color: rgba(255,255,255,0.96);
        }
        .s2-intro-subcopy {
          max-width: 760px;
          margin: -4px 0 0;
          color: rgba(255,255,255,0.6);
          font-family: 'Inter', sans-serif;
          font-size: clamp(15px, 1.55vw, 19px);
          font-weight: 400;
          line-height: 1.45;
          text-align: center;
        }
        /* ── Scroll spacers ── */
        .s2-feature-showcase {
          width: min(1120px, calc(100vw - 48px));
          margin-top: 14px;
          display: grid;
          grid-template-columns: repeat(12, minmax(0, 1fr));
          grid-auto-rows: 270px;
          gap: 18px;
          pointer-events: none;
        }
        .s2-feature-card {
          position: relative;
          overflow: hidden;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.22);
          background: rgba(8,20,32,0.12);
          box-shadow:
            0 26px 80px rgba(0,0,0,0.32);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          padding: 30px 28px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .s2-feature-card::before {
          content: "";
          position: absolute;
          inset: 1px;
          border-radius: 17px;
          pointer-events: none;
          z-index: 0;
          background: rgba(255,255,255,0.028);
          backdrop-filter: blur(46px) saturate(180%) brightness(1.1);
          -webkit-backdrop-filter: blur(46px) saturate(180%) brightness(1.1);
          opacity: 1;
        }
        .s2-feature-card.large { grid-column: span 7; }
        .s2-feature-card.medium { grid-column: span 5; }
        .s2-feature-card.wide { grid-column: span 7; }
        .s2-feature-card.trio { grid-column: span 4; }
        .s2-feature-card.text-only {
          justify-content: center;
          min-height: 0;
        }
        .s2-feature-title {
          position: relative;
          z-index: 2;
          color: #fff;
          font-family: 'Poppins', sans-serif;
          font-size: clamp(18px, 1.6vw, 24px);
          font-weight: 500;
          line-height: 1.1;
          letter-spacing: -0.02em;
          margin: 0 0 10px;
        }
        .s2-feature-copy {
          position: relative;
          z-index: 2;
          max-width: 470px;
          color: rgba(255,255,255,0.58);
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          line-height: 1.5;
          margin: 0 auto;
        }
        .s2-card-visual {
          position: absolute;
          inset: auto 28px 24px 28px;
          z-index: 1;
          height: 98px;
          border-radius: 14px;
          background: rgba(255,255,255,0.034);
          border: 1px solid rgba(255,255,255,0.105);
          box-shadow:
            0 12px 40px rgba(0,0,0,0.16);
          backdrop-filter: blur(30px) saturate(165%);
          -webkit-backdrop-filter: blur(30px) saturate(165%);
        }
        .s2-dashboard-visual {
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 10px;
          padding: 12px;
        }
        .s2-dashboard-panel {
          border-radius: 10px;
          background: linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04));
        }
        .s2-dashboard-panel.chart { position: relative; overflow: hidden; }
        .s2-dashboard-panel.chart::before {
          content: "";
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: 18px;
          height: 46px;
          background:
            linear-gradient(135deg, transparent 0 16%, rgba(255,64,64,0.9) 17% 20%, transparent 21% 38%, rgba(255,64,64,0.75) 39% 42%, transparent 43% 62%, rgba(255,64,64,0.85) 63% 66%, transparent 67%);
        }
        .s2-dashboard-panel.metrics {
          display: grid;
          gap: 8px;
          padding: 12px;
        }
        .s2-dashboard-panel.metrics span {
          display: block;
          height: 9px;
          border-radius: 99px;
          background: rgba(255,255,255,0.22);
        }
        .s2-history-visual {
          display: flex;
          align-items: end;
          gap: 8px;
          padding: 14px;
        }
        .s2-history-visual span {
          flex: 1;
          border-radius: 999px 999px 4px 4px;
          background: linear-gradient(180deg, rgba(255,80,80,0.85), rgba(255,80,80,0.18));
        }
        .s2-alert-visual {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .s2-alert-ring {
          width: 78px;
          height: 78px;
          border-radius: 50%;
          border: 1px solid rgba(255,64,64,0.55);
          box-shadow: 0 0 42px rgba(255,32,32,0.34), inset 0 0 28px rgba(255,32,32,0.18);
        }
        .s2-settings-visual {
          display: grid;
          gap: 10px;
          padding: 14px;
        }
        .s2-toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .s2-toggle-row span:first-child {
          height: 9px;
          flex: 1;
          border-radius: 999px;
          background: rgba(255,255,255,0.22);
        }
        .s2-toggle-row span:last-child {
          width: 34px;
          height: 18px;
          border-radius: 999px;
          background: rgba(255,64,64,0.58);
          box-shadow: inset 16px 0 0 rgba(255,255,255,0.72);
        }
        @media (max-width: 900px) {
          .s2-feature-showcase {
            grid-template-columns: 1fr;
            grid-auto-rows: 260px;
            width: min(560px, calc(100vw - 32px));
            margin-top: 22px;
          }
          .s2-feature-card.large,
          .s2-feature-card.medium,
          .s2-feature-card.wide,
          .s2-feature-card.trio {
            grid-column: span 1;
          }
        }
        .scroll-spacer { width: 100%; background: #000; }

        /* ── Demo section ── */
        .demo-section {
          position: relative;
          z-index: 20;
          overflow: visible;
          background:
            radial-gradient(circle at 88% 46%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.085) 14%, transparent 32%),
            #000;
          margin-top: -55vh;
          padding: 48px 40px clamp(150px, 18vh, 220px);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 56px;
        }
        .demo-section > * {
          position: relative;
          z-index: 1;
        }
        .demo-section::before {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          top: clamp(-260px, -22vh, -140px);
          height: clamp(220px, 30vh, 360px);
          z-index: 0;
          pointer-events: none;
          background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.34) 34%, rgba(0,0,0,0.82) 72%, #000 100%);
        }
        .demo-heading-block {
          text-align: center;
          max-width: 640px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0;
        }
        .demo-eyebrow {
          display: inline-block;
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #FF4444;
          margin-bottom: 16px;
          font-family: 'DM Sans', sans-serif;
        }
        .demo-heading {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(38px, 5vw, 62px);
          font-weight: 500;
          line-height: 0.95;
          letter-spacing: -1.5px;
          color: #fff;
          margin: 0;
          text-shadow: 0 2px 40px rgba(0,0,0,0.6);
        }
        .demo-heading .grad {
          background: linear-gradient(90deg, #FF2020, #FF8A00);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Tilt card */
        .demo-tilt-card {
          position: relative;
          width: 100%;
          max-width: 1080px;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 40px 120px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04);
          transition: transform 0.10s ease-out;
          transform-style: preserve-3d;
          background: #111;
          will-change: transform;
        }
        .demo-shine {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          border-radius: 20px;
          transition: background 0.05s;
        }
        .demo-chrome {
          background: #1a1a1a;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          padding: 10px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .demo-dot {
          width: 10px; height: 10px;
          border-radius: 50%;
          display: block;
          flex-shrink: 0;
        }
        .demo-url-bar {
          flex: 1;
          margin-left: 12px;
          background: rgba(255,255,255,0.06);
          border-radius: 6px;
          height: 22px;
          display: flex;
          align-items: center;
          padding-left: 10px;
        }
        .demo-url-text {
          font-size: 11px;
          color: rgba(255,255,255,0.3);
          font-family: 'DM Sans', sans-serif;
        }
        .demo-video {
          display: block;
          width: 100%;
          height: auto;
          max-height: 65vh;
          object-fit: cover;
        }
        .recommended-strip {
          position: relative;
          width: min(1080px, 100%);
          margin-top: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 28px;
        }
        .recommended-strip::before {
          content: "";
          position: absolute;
          left: 50%;
          top: 60%;
          width: min(1180px, 112vw);
          height: 380px;
          transform: translate(-50%, -50%);
          z-index: -1;
          pointer-events: none;
          background:
            radial-gradient(ellipse at center, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.12) 32%, rgba(255,255,255,0.04) 58%, rgba(255,255,255,0) 78%);
          filter: blur(14px);
        }
        .recommended-heading {
          max-width: 820px;
          margin: 0;
          color: rgba(255,255,255,0.78);
          font-family: 'Poppins', sans-serif;
          font-size: clamp(17px, 2vw, 24px);
          font-weight: 500;
          line-height: 1.35;
          letter-spacing: -0.01em;
          text-align: center;
        }
        .logo-marquee {
          position: relative;
          width: 100%;
          overflow: hidden;
          background: transparent;
        }
        .logo-marquee::before,
        .logo-marquee::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          width: clamp(64px, 12vw, 150px);
          z-index: 2;
          pointer-events: none;
        }
        .logo-marquee::before {
          left: 0;
          background: linear-gradient(90deg, #050505 0%, rgba(5,5,5,0.84) 36%, rgba(5,5,5,0) 100%);
        }
        .logo-marquee::after {
          right: 0;
          background: linear-gradient(270deg, #050505 0%, rgba(5,5,5,0.84) 36%, rgba(5,5,5,0) 100%);
        }
        .logo-marquee-track {
          display: flex;
          width: max-content;
          gap: 2px;
          padding: 16px 10px;
          will-change: transform;
          animation: logo-scroll 18s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
        }
        .logo-item {
          width: auto;
          height: 142px;
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 8px;
          opacity: 0.84;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .logo-item:hover {
          opacity: 1;
          transform: translateY(-1px);
        }
        .logo-item img {
          width: auto;
          max-width: none;
          max-height: 112px;
          object-fit: contain;
          filter: grayscale(1) brightness(1.6) contrast(0.9);
        }
        @keyframes logo-scroll {
          from { transform: translate3d(0, 0, 0); }
          72% { transform: translate3d(-43%, 0, 0); }
          88% { transform: translate3d(-51.8%, 0, 0); }
          to { transform: translate3d(-50%, 0, 0); }
        }
        @media (max-width: 760px) {
          .recommended-strip {
            gap: 18px;
          }
          .logo-marquee-track {
            gap: 2px;
            padding: 12px 8px;
            animation-duration: 20s;
          }
          .logo-item {
            width: auto;
            height: 116px;
            padding: 0 6px;
          }
          .logo-item img {
            max-height: 88px;
          }
        }
        .demo-pill-row {
          display: flex;
          justify-content: center;
          gap: 14px;
          flex-wrap: wrap;
          margin-top: -28px;
        }
        .demo-pill {
          min-width: 150px;
          padding: 13px 28px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.18);
          background: linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.035));
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.16),
            0 18px 48px rgba(0,0,0,0.32);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          color: rgba(255,255,255,0.88);
          font-family: 'Poppins', sans-serif;
          font-size: 13px;
          font-weight: 300;
          line-height: 1;
          text-align: center;
          text-transform: capitalize;
          white-space: nowrap;
        }
        .demo-dither {
          position: absolute;
          inset: 0;
          z-index: 0;
          width: 100%;
          height: 100%;
          opacity: 0.78;
          mix-blend-mode: screen;
          display: block;
          pointer-events: none;
        }
        .demo-reveal {
          opacity: 0;
          filter: blur(8px);
          transform: translate3d(0, 18px, 0);
          transition:
            opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, filter, transform;
        }
        .demo-reveal.is-visible {
          opacity: 1;
          filter: blur(0);
          transform: translate3d(0, 0, 0) scale(1);
        }
        .demo-pill.demo-reveal {
          transform: translate3d(0, 12px, 0);
          transition-duration: 0.72s;
        }
        .recommended-strip.demo-reveal {
          transform: translate3d(0, 20px, 0);
        }
        .live-demo-section {
          position: relative;
          z-index: 20;
          min-height: 100vh;
          overflow: hidden;
          padding: clamp(118px, 16vh, 170px) 40px clamp(120px, 15vh, 170px);
          display: flex;
          align-items: center;
          color: #fff;
          background: #030303;
        }
        .live-demo-section::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          background: url('/livedemo.jpg') center / cover no-repeat;
          filter: brightness(0.34) contrast(0.72) saturate(0.9);
          transform: scale(1.04);
        }
        .live-demo-section::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background:
            linear-gradient(180deg, #000 0%, rgba(0,0,0,0.78) 8%, rgba(0,0,0,0.28) 24%, rgba(0,0,0,0.34) 72%, #000 100%),
            radial-gradient(circle at 20% 40%, rgba(255,64,64,0.14), transparent 36%),
            linear-gradient(90deg, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.12) 48%, rgba(0,0,0,0.54) 100%);
        }
        .live-demo-inner {
          position: relative;
          z-index: 1;
          width: min(1320px, 100%);
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
          gap: clamp(42px, 6vw, 86px);
          align-items: center;
        }
        .live-demo-video-wrap {
          position: relative;
          overflow: hidden;
          aspect-ratio: 16 / 9;
          background: rgba(0,0,0,0.64);
          box-shadow:
            0 34px 90px rgba(0,0,0,0.58),
            0 0 0 1px rgba(255,255,255,0.1);
        }
        .live-demo-video-wrap iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }
        .live-demo-copy {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .live-demo-eyebrow {
          margin-bottom: 22px;
          color: #FF5252;
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        .live-demo-quote {
          margin: 0;
          max-width: 600px;
          color: rgba(255,255,255,0.92);
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 3.1vw, 44px);
          font-weight: 500;
          font-style: italic;
          line-height: 1.06;
          text-shadow: 0 4px 42px rgba(0,0,0,0.72);
        }
        .live-demo-caption {
          max-width: 560px;
          margin: 28px 0 0;
          color: rgba(255,255,255,0.68);
          font-family: 'Inter', sans-serif;
          font-size: clamp(15px, 1.25vw, 18px);
          font-weight: 400;
          line-height: 1.58;
        }
        .live-demo-meta {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 28px;
          width: 100%;
          margin-top: 34px;
        }
        .live-demo-name {
          display: block;
          color: rgba(255,255,255,0.92);
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 500;
          line-height: 1.2;
        }
        .live-demo-role {
          display: block;
          margin-top: 6px;
          color: rgba(255,255,255,0.5);
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          line-height: 1.35;
        }
        .live-demo-brand {
          color: rgba(255,255,255,0.54);
          font-family: 'Poppins', sans-serif;
          font-size: clamp(24px, 2.7vw, 38px);
          font-weight: 600;
          letter-spacing: -0.04em;
          line-height: 0.9;
        }
        .live-demo-reveal {
          opacity: 0;
          filter: blur(10px);
          transform: translate3d(0, 26px, 0);
          transition:
            opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, filter, transform;
        }
        .live-demo-reveal.is-visible {
          opacity: 1;
          filter: blur(0);
          transform: translate3d(0, 0, 0);
        }
        .pilot-section {
          position: relative;
          z-index: 20;
          min-height: 100vh;
          overflow: hidden;
          padding: clamp(112px, 15vh, 160px) 0 clamp(120px, 16vh, 180px);
          display: flex;
          align-items: center;
          color: #fff;
          background: #030303;
        }
        .pilot-section::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          background: url('/pilottest.jpg') center / cover no-repeat;
          filter: brightness(0.34) contrast(0.72) saturate(0.9);
          transform: scale(1.04);
        }
        .pilot-section::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background:
            linear-gradient(180deg, #000 0%, rgba(0,0,0,0.78) 8%, rgba(0,0,0,0.28) 24%, rgba(0,0,0,0.36) 72%, #000 100%),
            radial-gradient(circle at 70% 28%, rgba(255,96,32,0.14), transparent 34%),
            linear-gradient(90deg, rgba(0,0,0,0.56) 0%, rgba(0,0,0,0.18) 44%, rgba(0,0,0,0.56) 100%);
        }
        .pilot-inner {
          position: relative;
          z-index: 1;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: clamp(34px, 5vw, 58px);
        }
        .pilot-heading-block {
          width: min(760px, calc(100% - 40px));
          text-align: center;
        }
        .pilot-eyebrow {
          display: block;
          margin-bottom: 14px;
          color: #FF7A2F;
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        .pilot-heading {
          margin: 0;
          color: rgba(255,255,255,0.94);
          font-family: 'Poppins', sans-serif;
          font-size: clamp(34px, 4.4vw, 62px);
          font-weight: 500;
          line-height: 0.98;
          letter-spacing: 0;
          text-shadow: 0 4px 42px rgba(0,0,0,0.72);
        }
        .pilot-copy {
          max-width: 620px;
          margin: 18px auto 0;
          color: rgba(255,255,255,0.64);
          font-family: 'Inter', sans-serif;
          font-size: clamp(15px, 1.2vw, 18px);
          line-height: 1.58;
        }
        .pilot-carousel {
          width: min(1120px, 100%);
          overflow: hidden;
          min-height: clamp(230px, 24vw, 330px);
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%);
        }
        .pilot-carousel-track {
          display: flex;
          width: max-content;
          gap: clamp(10px, 1.25vw, 18px);
          padding: clamp(12px, 2vw, 24px) 0 clamp(18px, 3vw, 34px);
          animation: pilot-scroll 34s linear infinite;
          will-change: transform;
        }
        .pilot-carousel:hover .pilot-carousel-track {
          animation-play-state: paused;
        }
        .pilot-card {
          position: relative;
          flex: 0 0 clamp(150px, 14vw, 220px);
          height: clamp(210px, 20vw, 310px);
          overflow: hidden;
          border-radius: 22px;
          background: rgba(0,0,0,0.42);
          box-shadow:
            0 34px 90px rgba(0,0,0,0.58),
            0 12px 30px rgba(0,0,0,0.34),
            0 0 0 1px rgba(255,255,255,0.12);
          transform: translateY(0) scale(1);
          transform-origin: center center;
          transition:
            transform 0.32s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.32s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.32s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .pilot-card:hover {
          z-index: 4;
          transform: translateY(-18px) scale(1.04);
          box-shadow:
            0 46px 120px rgba(0,0,0,0.68),
            0 18px 42px rgba(0,0,0,0.42),
            0 0 0 1px rgba(255,255,255,0.2);
        }
        .pilot-card img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          filter: saturate(0.92) contrast(0.92) brightness(0.9);
          transition: filter 0.32s ease, transform 0.32s ease;
        }
        .pilot-card:hover img {
          filter: saturate(1.04) contrast(0.98) brightness(1);
          transform: scale(1.04);
        }
        .pilot-card::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(180deg, rgba(255,255,255,0.12), transparent 28%, rgba(0,0,0,0.22) 100%);
        }
        .pilot-steps {
          width: min(980px, calc(100% - 40px));
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 18px;
          text-align: center;
        }
        .pilot-step-number {
          display: block;
          color: #FF7A2F;
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          font-weight: 700;
          line-height: 1;
        }
        .pilot-step-label {
          display: block;
          margin-top: 8px;
          color: rgba(255,255,255,0.62);
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          line-height: 1.3;
        }
        .pilot-reveal {
          opacity: 0;
          filter: blur(10px);
          transform: translate3d(0, 28px, 0);
          transition:
            opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, filter, transform;
        }
        .pilot-reveal.is-visible {
          opacity: 1;
          filter: blur(0);
          transform: translate3d(0, 0, 0);
        }
        .tech-stack-section {
          position: relative;
          z-index: 20;
          min-height: 100vh;
          overflow: hidden;
          padding: clamp(112px, 14vh, 168px) clamp(20px, 5vw, 72px);
          display: flex;
          align-items: center;
          color: #fff;
          background: #000;
          isolation: isolate;
        }
        .tech-stack-section::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -2;
          background: url('/techstack.jpg') center / cover no-repeat;
          filter: brightness(0.28) contrast(0.78) saturate(0.82);
          transform: scale(1.04);
        }
        .tech-stack-section::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          pointer-events: none;
          background:
            linear-gradient(180deg, #000 0%, rgba(0,0,0,0.86) 10%, rgba(0,0,0,0.34) 28%, rgba(0,0,0,0.38) 70%, #000 100%),
            radial-gradient(circle at 70% 50%, rgba(66,133,244,0.16), transparent 34%),
            radial-gradient(circle at 34% 32%, rgba(238,76,44,0.12), transparent 30%),
            linear-gradient(90deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.22) 52%, rgba(0,0,0,0.58) 100%);
        }
        .tech-stack-inner {
          width: min(1120px, 100%);
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 0.88fr) minmax(420px, 1.12fr);
          gap: clamp(40px, 8vw, 96px);
          align-items: center;
        }
        .tech-stack-copy {
          max-width: 520px;
        }
        .tech-stack-eyebrow {
          display: block;
          margin-bottom: 16px;
          color: #FF7A2F;
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        .tech-stack-heading {
          margin: 0;
          color: rgba(255,255,255,0.94);
          font-family: 'Playfair Display', serif;
          font-size: clamp(36px, 5.2vw, 76px);
          font-style: italic;
          font-weight: 500;
          line-height: 1.02;
          letter-spacing: 0;
          text-shadow: 0 4px 42px rgba(0,0,0,0.72);
        }
        .tech-stack-copy-text {
          max-width: 500px;
          margin: 24px 0 0;
          color: rgba(255,255,255,0.68);
          font-family: 'Inter', sans-serif;
          font-size: clamp(15px, 1.2vw, 18px);
          line-height: 1.58;
        }
        .tech-stack-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 30px;
        }
        .tech-stack-tags span {
          border: 1px solid rgba(255,255,255,0.16);
          border-radius: 999px;
          padding: 8px 12px;
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.72);
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          line-height: 1;
          backdrop-filter: blur(14px);
        }
        .tech-stack-orbit-wrap {
          min-height: 560px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tech-orbit-stage {
          position: relative;
          width: min(560px, calc(100vw - 48px));
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tech-orbit-stage::before {
          content: "";
          position: absolute;
          inset: 14%;
          border-radius: 50%;
          background:
            radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 42%, transparent 70%);
          filter: blur(1px);
        }
        .tech-orbit-core {
          position: relative;
          z-index: 5;
          width: 92px;
          height: 92px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(0,0,0,0.72);
          border: 1px solid rgba(255,255,255,0.16);
          box-shadow:
            0 22px 60px rgba(0,0,0,0.62),
            0 0 48px rgba(255,122,47,0.16),
            inset 0 0 24px rgba(255,255,255,0.06);
          backdrop-filter: blur(18px);
        }
        .tech-orbit-core img {
          width: 42px;
          height: 42px;
          object-fit: contain;
        }
        .tech-orbit-path {
          position: absolute;
          top: 50%;
          left: 50%;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.12);
          transform: translate(-50%, -50%);
          pointer-events: none;
          box-shadow:
            inset 0 0 38px rgba(255,255,255,0.04),
            0 0 38px rgba(66,133,244,0.1);
        }
        .tech-orbit-path.inner {
          width: 200px;
          height: 200px;
          border-color: rgba(255,122,47,0.26);
          box-shadow:
            inset 0 0 34px rgba(255,122,47,0.1),
            0 0 42px rgba(255,122,47,0.12);
        }
        .tech-orbit-path.outer {
          width: 360px;
          height: 360px;
          border-color: rgba(59,168,255,0.24);
          box-shadow:
            inset 0 0 38px rgba(59,168,255,0.08),
            0 0 44px rgba(59,168,255,0.12);
        }
        .tech-orbit-skill {
          position: absolute;
          top: 50%;
          left: 50%;
          z-index: 4;
          width: var(--skill-size);
          height: var(--skill-size);
          transform: translate(calc(var(--skill-x) - 50%), calc(var(--skill-y) - 50%));
          transition: transform 0.22s ease, z-index 0.22s ease;
        }
        .tech-orbit-icon {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          padding: 10px;
          background: rgba(7,7,9,0.86);
          border: 1px solid rgba(255,255,255,0.16);
          box-shadow:
            0 18px 42px rgba(0,0,0,0.46),
            0 0 28px color-mix(in srgb, var(--skill-color) 24%, transparent);
          backdrop-filter: blur(14px);
          transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease;
        }
        .tech-orbit-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }
        .tech-orbit-label {
          position: absolute;
          top: calc(100% + 9px);
          left: 50%;
          transform: translateX(-50%) translateY(6px);
          opacity: 0;
          white-space: nowrap;
          border-radius: 999px;
          padding: 6px 9px;
          background: rgba(0,0,0,0.78);
          border: 1px solid rgba(255,255,255,0.14);
          color: rgba(255,255,255,0.86);
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          line-height: 1;
          pointer-events: none;
          transition: opacity 0.22s ease, transform 0.22s ease;
        }
        .tech-orbit-skill:hover {
          z-index: 12;
          transform: translate(calc(var(--skill-x) - 50%), calc(var(--skill-y) - 50%)) scale(1.12);
        }
        .tech-orbit-skill:hover .tech-orbit-icon {
          border-color: color-mix(in srgb, var(--skill-color) 58%, white);
          box-shadow:
            0 24px 54px rgba(0,0,0,0.54),
            0 0 38px color-mix(in srgb, var(--skill-color) 40%, transparent);
        }
        .tech-orbit-skill:hover .tech-orbit-label {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
        .tech-reveal {
          opacity: 0;
          filter: blur(10px);
          transform: translate3d(0, 30px, 0);
          transition:
            opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.9s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, filter, transform;
        }
        .tech-reveal.is-visible {
          opacity: 1;
          filter: blur(0);
          transform: translate3d(0, 0, 0);
        }
        .site-footer {
          position: relative;
          z-index: 20;
          background: #0b0b0c;
          color: #fff;
          padding: clamp(64px, 9vw, 96px) clamp(20px, 5vw, 72px) 38px;
          font-family: 'Inter', sans-serif;
        }
        .site-footer-inner {
          width: min(1120px, 100%);
          margin: 0 auto;
        }
        .site-footer-grid {
          display: grid;
          grid-template-columns: minmax(220px, 1.15fr) repeat(3, minmax(140px, 0.8fr));
          gap: clamp(34px, 6vw, 88px);
          align-items: start;
        }
        .site-footer-brand {
          display: flex;
          flex-direction: column;
          gap: 18px;
          max-width: 310px;
        }
        .site-footer-logo {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: #fff;
          text-decoration: none;
        }
        .site-footer-logo img:first-child {
          width: 34px;
          height: 34px;
          object-fit: contain;
        }
        .site-footer-logo img:last-child {
          height: 22px;
          width: auto;
          object-fit: contain;
        }
        .site-footer-copy {
          margin: 0;
          color: rgba(255,255,255,0.58);
          font-size: 14px;
          line-height: 1.62;
        }
        .site-footer-team {
          margin: 0;
          color: rgba(255,255,255,0.76);
          font-size: 13px;
          font-weight: 600;
        }
        .site-footer-column-title {
          display: block;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.94);
          font-size: 16px;
          font-weight: 700;
        }
        .site-footer-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .site-footer-link {
          color: rgba(255,255,255,0.58);
          font-size: 14px;
          line-height: 1.25;
          text-decoration: none;
          transition: color 0.18s ease, transform 0.18s ease;
        }
        .site-footer-link:hover {
          color: #fff;
          transform: translateX(2px);
        }
        .site-footer-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-top: clamp(54px, 7vw, 76px);
          padding-top: 28px;
          border-top: 1px solid rgba(255,255,255,0.14);
          color: rgba(255,255,255,0.58);
          font-size: 13px;
          line-height: 1.4;
        }
        .site-footer-bottom p {
          margin: 0;
        }
        @keyframes pilot-scroll {
          from { transform: translate3d(0, 0, 0); }
          to { transform: translate3d(calc(-50% - clamp(7px, 1vw, 12px)), 0, 0); }
        }
        @media (max-width: 960px) {
          .live-demo-section {
            min-height: auto;
            padding: 110px 22px 120px;
          }
          .live-demo-inner {
            grid-template-columns: 1fr;
            gap: 38px;
          }
          .live-demo-meta {
            align-items: center;
            flex-direction: column;
          }
          .pilot-section {
            min-height: auto;
            padding-top: 104px;
            padding-bottom: 112px;
          }
          .pilot-steps {
            grid-template-columns: repeat(2, 1fr);
            gap: 22px 14px;
          }
          .tech-stack-section {
            min-height: auto;
            padding-top: 108px;
            padding-bottom: 118px;
          }
          .tech-stack-inner {
            grid-template-columns: 1fr;
            gap: 42px;
          }
          .tech-stack-copy {
            max-width: 680px;
            text-align: center;
            justify-self: center;
          }
          .tech-stack-copy-text {
            margin-left: auto;
            margin-right: auto;
          }
          .tech-stack-tags {
            justify-content: center;
          }
          .tech-stack-orbit-wrap {
            min-height: 440px;
          }
          .tech-orbit-stage {
            transform: scale(0.86);
          }
          .site-footer-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .site-footer-brand {
            grid-column: 1 / -1;
            max-width: 540px;
          }
          .site-footer-bottom {
            align-items: flex-start;
            flex-direction: column;
          }
        }
        @media (max-width: 560px) {
          .live-demo-section {
            padding-left: 16px;
            padding-right: 16px;
          }
          .live-demo-quote {
            font-size: clamp(26px, 9vw, 36px);
          }
          .live-demo-caption {
            margin-top: 22px;
          }
          .live-demo-brand {
            font-size: 28px;
          }
          .pilot-heading-block {
            width: min(100% - 32px, 520px);
          }
          .pilot-heading {
            font-size: clamp(31px, 10vw, 44px);
          }
          .pilot-carousel {
            min-height: 250px;
            -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 5%, #000 95%, transparent 100%);
            mask-image: linear-gradient(90deg, transparent 0%, #000 5%, #000 95%, transparent 100%);
          }
          .pilot-card {
            flex-basis: 160px;
            height: 226px;
            border-radius: 16px;
          }
          .tech-stack-section {
            padding-left: 16px;
            padding-right: 16px;
          }
          .tech-stack-heading {
            font-size: clamp(31px, 10vw, 44px);
          }
          .tech-stack-orbit-wrap {
            min-height: 360px;
          }
          .tech-orbit-stage {
            transform: scale(0.68);
          }
          .site-footer {
            padding-top: 58px;
          }
          .site-footer-grid {
            grid-template-columns: 1fr;
            gap: 34px;
          }
          .site-footer-column-title {
            margin-bottom: 14px;
          }
        }
        .s2-reveal {
          opacity: 0;
          filter: blur(10px);
          transform: translate3d(0, 26px, 0) scale(0.985);
          transition:
            opacity 0.86s cubic-bezier(0.22, 1, 0.36, 1),
            filter 0.86s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.86s cubic-bezier(0.22, 1, 0.36, 1);
          transition-delay: var(--reveal-delay, 0ms);
          will-change: opacity, filter, transform;
        }
        .s2-reveal.is-visible {
          opacity: 1;
          filter: blur(0);
          transform: translate3d(0, 0, 0) scale(1);
        }
        .s2-feature-card.s2-reveal {
          transform: translate3d(0, 32px, 0) scale(0.975);
        }
        @media (prefers-reduced-motion: reduce) {
          .demo-reveal,
          .demo-reveal.is-visible,
          .demo-pill.demo-reveal,
          .recommended-strip.demo-reveal,
          .live-demo-reveal,
          .live-demo-reveal.is-visible,
          .pilot-reveal,
          .pilot-reveal.is-visible,
          .tech-reveal,
          .tech-reveal.is-visible,
          .s2-reveal,
          .s2-reveal.is-visible,
          .s2-feature-card.s2-reveal {
            opacity: 1;
            filter: none;
            transform: none;
            transition: none;
          }
          .tech-orbit-skill,
          .tech-orbit-skill:hover {
            transform: translate(calc(var(--skill-x) - 50%), calc(var(--skill-y) - 50%));
          }
        }
      `}</style>

      {/* ── Loader ─────────────────────────────────────────────────────────── */}
      <div ref={loaderRef} className="loader-overlay">
        <span className="loader-label">Loading</span>
        <div className="loader-track"><div ref={loaderBarRef} className="loader-bar" /></div>
      </div>

      {/* ── Nav ────────────────────────────────────────────────────────────── */}
      <nav id="birdseye-nav">
        <div className="nav-inner">
          <button className="btn-ghost nav-brand" onClick={() => navigate('/')}>
            <img src="/images/logo.png"      alt="" style={{ height: 24 }} />
            <img src="/images/logo-text.png" alt="Birdseye" style={{ height: 20 }} />
          </button>
          <ul className="nav-links">
            <li className="features-nav-item">
              <a href="#features" aria-haspopup="true">Features</a>
              <div className="features-mega" role="menu" aria-label="Features">
                <div className="features-mega-column">
                  <span className="features-mega-heading">Monitoring</span>
                  <ul className="features-menu-list">
                    {FEATURE_USE_CASES.map((item) => (
                      <li key={item.title}>
                        <a className="features-menu-link" href="#features" role="menuitem">
                          <span className="features-menu-icon">
                            <FeatureMenuIcon type={item.icon} />
                          </span>
                          <span>
                            <span className="features-menu-title">{item.title}</span>
                            <span className="features-menu-copy">{item.copy}</span>
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="features-mega-column">
                  <span className="features-mega-heading">Insights</span>
                  <ul className="features-menu-list">
                    {FEATURE_INDUSTRIES.map((item) => (
                      <li key={item.title}>
                        <a className="features-menu-link" href="#features" role="menuitem">
                          <span className="features-menu-icon">
                            <FeatureMenuIcon type={item.icon} />
                          </span>
                          <span>
                            <span className="features-menu-title">{item.title}</span>
                            <span className="features-menu-copy">{item.copy}</span>
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <aside className="features-mega-promo" aria-label="Birdseye for enterprise">
                  <div className="features-promo-image" aria-hidden="true" />
                  <h3 className="features-promo-brand">Birdseye <em>Enterprise</em></h3>
                  <p className="features-promo-copy">
                    Scale farm monitoring with enterprise-grade alerts, teams, and custom permissions.
                  </p>
                  <button className="features-promo-button" type="button">Learn More</button>
                </aside>
              </div>
            </li>
            {['How it works', 'Pricing', 'Contact us'].map((t) => (
              <li key={t}><a href={`#${t.toLowerCase().replace(/ /g, '-')}`}>{t}</a></li>
            ))}
          </ul>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Log in</button>
            <button className="btn-nav"   onClick={() => navigate('/signup')}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* ════════════════════════════════════════════════════════════════════
          SCENE 1
      ════════════════════════════════════════════════════════════════════ */}
      <div ref={s1WrapperRef} className="sticky-wrapper" style={{ height: sceneScrollHeight(S1_SCROLL) }}>
        <div ref={s1SceneRef} className="sticky-scene">
          <canvas ref={s1CanvasRef} className="sticky-canvas" />
          <div ref={s1VigRef} className="bottom-vignette" />

          {/* Intro — fades out as you scroll */}
          <div ref={s1IntroRef} className="overlay-text intro-text">
            <h1
              className="intro-headline"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              <span className="intro-reveal-piece">The eyes poultry farms</span><br />
              <span
                className="intro-reveal-piece"
                style={{
                  background: 'linear-gradient(90deg, #FF2020, #FF8A00)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                don't have.
              </span>
            </h1>
          </div>

          {/* Hero — fades in at end of Scene 1, slides up during crossfade */}
          <div ref={s1HeroRef} className="overlay-text hero-text">
            <h2 className="hero-headline">
              <span className="accent">Detect.</span> Alert.<br />Intervene.
            </h2>
            <p className="hero-sub">
              Automated monitoring and alerts for<br />poultry litter management.
            </p>
            <div className="hero-cta-row">
              <button className="btn-cta-primary" onClick={() => navigate('/signup')}>Get Started</button>
              <button className="btn-cta-ghost">&#9654;&nbsp; Watch demo</button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          SCENE 2
      ════════════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="demo-section">
        <canvas ref={ditherCanvasRef} className="demo-dither" aria-hidden="true" />

        <div className="demo-heading-block demo-reveal">
          <span className="demo-eyebrow">See it in action</span>
          <h2 className="demo-heading">
            Monitor every coop,<br />
            <span className="grad">from anywhere.</span>
          </h2>
        </div>

        <div
          className="demo-tilt-card demo-reveal"
          style={{ '--reveal-delay': '120ms' }}
          onMouseMove={handleTiltMove}
          onMouseLeave={handleTiltLeave}
        >
          <div className="demo-shine" />

          <div className="demo-chrome">
            <span className="demo-dot" style={{ background: '#FF5F57' }} />
            <span className="demo-dot" style={{ background: '#FFBD2E' }} />
            <span className="demo-dot" style={{ background: '#27C93F' }} />
            <div className="demo-url-bar">
              <span className="demo-url-text">birdseye.work/dashboard</span>
            </div>
          </div>

          <video
            className="demo-video"
            src="/demo.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
        </div>

        <div className="demo-pill-row" aria-label="Birdseye capabilities">
          {['monitoring', 'detection', 'recommendation'].map((label, index) => (
            <span
              className="demo-pill demo-reveal"
              key={label}
              style={{ '--reveal-delay': `${220 + index * 90}ms` }}
            >
              {label}
            </span>
          ))}
        </div>

        <section className="recommended-strip demo-reveal" aria-label="Recommended by" style={{ '--reveal-delay': '180ms' }}>
          <h3 className="recommended-heading">Validated by the pioneers shaping Philippine agriculture:</h3>
          <div className="logo-marquee">
            <div className="logo-marquee-track">
              {[...RECOMMENDED_LOGOS, ...RECOMMENDED_LOGOS].map((logo, index) => (
                <div className="logo-item" key={`${logo.name}-${index}`}>
                  <img src={logo.src} alt={logo.name} loading="lazy" decoding="async" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </section>

      <div ref={s2WrapperRef} className="sticky-wrapper" style={{ height: sceneScrollHeight(S2_SCROLL) }}>
        <div ref={s2SceneRef} className="sticky-scene scene-2">
          <canvas ref={s2CanvasRef} className="sticky-canvas" />
          <div ref={s2VigRef} className="bottom-vignette" />

          {/* Scene 2 intro — scrolls upward (no fade) */}
          <div ref={s2IntroRef} className="overlay-text intro-text" style={{ opacity: 1 }}>
            <h2 className="s2-intro-headline s2-reveal">
              Real-time insights. <strong>Zero guesswork.</strong>
            </h2>
            <p className="s2-intro-subcopy s2-reveal" style={{ '--reveal-delay': '70ms' }}>
              Monitor every coop, catch what matters, and act before litter risks spread. All in one place.
            </p>
            <div className="s2-feature-showcase" aria-label="Birdseye feature showcase">
              <article className="s2-feature-card large s2-reveal" style={{ '--reveal-delay': '110ms' }}>
                <h3 className="s2-feature-title">Real-time Detection</h3>
                <p className="s2-feature-copy">Spot litter risks and abnormal coop conditions as they start to form.</p>
                <div className="s2-card-visual s2-dashboard-visual" aria-hidden="true">
                  <div className="s2-dashboard-panel chart" />
                  <div className="s2-dashboard-panel metrics">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </article>

              <article className="s2-feature-card medium s2-reveal" style={{ '--reveal-delay': '190ms' }}>
                <h3 className="s2-feature-title">Ammonia Monitoring</h3>
                <p className="s2-feature-copy">Track ammonia risk patterns before they affect flock health and comfort.</p>
                <div className="s2-card-visual s2-history-visual" aria-hidden="true">
                  <span style={{ height: '34%' }} />
                  <span style={{ height: '58%' }} />
                  <span style={{ height: '42%' }} />
                  <span style={{ height: '74%' }} />
                  <span style={{ height: '64%' }} />
                </div>
              </article>

              <article className="s2-feature-card medium s2-reveal" style={{ '--reveal-delay': '270ms' }}>
                <h3 className="s2-feature-title">History</h3>
                <p className="s2-feature-copy">Review past trends, events, and litter condition changes over time.</p>
                <div className="s2-card-visual s2-history-visual" aria-hidden="true">
                  <span style={{ height: '44%' }} />
                  <span style={{ height: '62%' }} />
                  <span style={{ height: '38%' }} />
                  <span style={{ height: '78%' }} />
                  <span style={{ height: '56%' }} />
                </div>
              </article>

              <article className="s2-feature-card wide s2-reveal" style={{ '--reveal-delay': '350ms' }}>
                <h3 className="s2-feature-title">Actionable Insights</h3>
                <p className="s2-feature-copy">Turn sensor readings into clear next steps for faster intervention.</p>
                <div className="s2-card-visual s2-dashboard-visual" aria-hidden="true">
                  <div className="s2-dashboard-panel chart" />
                  <div className="s2-dashboard-panel metrics">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </article>

              <article className="s2-feature-card trio text-only s2-reveal" style={{ '--reveal-delay': '430ms' }}>
                <h3 className="s2-feature-title">Mechanical Leak Detection</h3>
                <p className="s2-feature-copy">Flag likely drinker or line leaks that can turn bedding wet fast.</p>
              </article>

              <article className="s2-feature-card trio text-only s2-reveal" style={{ '--reveal-delay': '510ms' }}>
                <h3 className="s2-feature-title">Critical SMS Alerts</h3>
                <p className="s2-feature-copy">Send urgent warnings to the right people when conditions cross limits.</p>
              </article>

              <article className="s2-feature-card trio text-only s2-reveal" style={{ '--reveal-delay': '590ms' }}>
                <h3 className="s2-feature-title">Settings</h3>
                <p className="s2-feature-copy">Configure thresholds, notification rules, and monitoring preferences.</p>
              </article>
            </div>
          </div>

        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
      ════════════════════════════════════════════════════════════════════ */}
          {/* Shine overlay — tracks mouse position */}
      <section id="live-demo" className="live-demo-section" aria-labelledby="live-demo-heading">
        <div className="live-demo-inner">
          <div className="live-demo-video-wrap live-demo-reveal">
            <iframe
              src="https://www.youtube.com/embed/HppmiX_1wfA"
              title="Birdseye live demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>

          <div className="live-demo-copy live-demo-reveal" style={{ '--reveal-delay': '120ms' }}>
            <span className="live-demo-eyebrow">Live demo</span>
            <h2 id="live-demo-heading" className="live-demo-quote">
              Watch how Birdseye actually works in your farm, from live monitoring to faster decisions when litter conditions start to change.
            </h2>
            <p className="live-demo-caption">
              See the flow from detection to alerts, recommendations, and farm-level visibility. This is the practical side of Birdseye: less guessing, faster action, and clearer oversight for every coop.
            </p>
          </div>
        </div>
      </section>

      <section id="pilot-testing" className="pilot-section" aria-labelledby="pilot-testing-heading">
        <div className="pilot-inner">
          <div className="pilot-heading-block pilot-reveal">
            <span className="pilot-eyebrow">Pilot testing</span>
            <h2 id="pilot-testing-heading" className="pilot-heading">
              Field-tested with real farm conditions.
            </h2>
            <p className="pilot-copy">
              Before Birdseye reaches wider deployment, we tested the system in real-life farm conditions with our partner, the National Swine and Poultry Research Development Center, and their farmers in Tiaong, Quezon.
            </p>
          </div>

          <div className="pilot-carousel pilot-reveal" style={{ '--reveal-delay': '120ms' }} aria-label="Pilot testing photo carousel">
            <div className="pilot-carousel-track">
              {[...PILOT_TEST_IMAGES, ...PILOT_TEST_IMAGES].map((src, index) => (
                <figure className="pilot-card" key={`${src}-${index}`}>
                  <img src={src} alt={`Birdseye pilot testing ${index % PILOT_TEST_IMAGES.length + 1}`} loading="lazy" decoding="async" />
                </figure>
              ))}
            </div>
          </div>

          <div className="pilot-steps pilot-reveal" style={{ '--reveal-delay': '180ms' }} aria-label="Pilot testing stats">
            {[
              ['60', 'Professionals tested with'],
              ['4.5', 'ISO/IEC 25023 ratings'],
              ['89%+', 'Accuracy'],
              ['72 hours', 'Continuous testing'],
            ].map(([value, label]) => (
              <span key={label}>
                <span className="pilot-step-number">{value}</span>
                <span className="pilot-step-label">{label}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="tech-stack" className="tech-stack-section" aria-labelledby="tech-stack-heading">
        <div className="tech-stack-inner">
          <div className="tech-stack-copy tech-reveal">
            <span className="tech-stack-eyebrow">Tech stack</span>
            <h2 id="tech-stack-heading" className="tech-stack-heading">
              Built with the tools behind reliable farm intelligence.
            </h2>
            <p className="tech-stack-copy-text">
              Birdseye was built on a cloud-hosted stack using React, FastAPI, and Neon PostgreSQL, driven by a hybrid AI pipeline combining YOLOv11 detection, U-Net segmentation, and a custom SLER algorithm for real-time wet litter analysis.
            </p>
            <div className="tech-stack-tags" aria-label="Birdseye technology stack list">
              {TECH_STACK_SKILLS.map((skill) => (
                <span key={skill.id}>{skill.label}</span>
              ))}
            </div>
          </div>

          <div className="tech-stack-orbit-wrap tech-reveal" style={{ '--reveal-delay': '140ms' }}>
            <TechStackOrbit />
          </div>
        </div>
      </section>

      <footer className="site-footer" aria-labelledby="footer-heading">
        <div className="site-footer-inner">
          <div className="site-footer-grid">
            <div className="site-footer-brand">
              <a className="site-footer-logo" href="/" aria-label="Birdseye home">
                <img src="/images/logo.png" alt="" loading="lazy" decoding="async" />
                <img src="/images/logo-text.png" alt="Birdseye" loading="lazy" decoding="async" />
              </a>
              <p id="footer-heading" className="site-footer-copy">
                Cloud-hosted poultry monitoring for faster detection, clearer alerts, and better farm decisions.
              </p>
              <p className="site-footer-team">Built by NERDS 2.0.</p>
            </div>

            {FOOTER_LINK_GROUPS.map((group) => (
              <div className="site-footer-column" key={group.title}>
                <span className="site-footer-column-title">{group.title}</span>
                <ul className="site-footer-list">
                  {group.links.map((link) => (
                    <li key={`${group.title}-${link.label}`}>
                      <a className="site-footer-link" href={link.href}>{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="site-footer-bottom">
            <p>© 2026 Team NERDS 2.0. All rights reserved.</p>
            <p>All trademarks are the property of their respective owners.</p>
          </div>
        </div>
      </footer>

      {/* ── More sections (Features, Pricing, etc.) go here ──────────────── */}
    </>
  );
};

export default LandingPage;
